import type { PreloadedQuery } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import QUERY, {
	type GlobalProductsSettingsSidebarQuery,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-products/src/ui/__generated__/GlobalProductsSettingsSidebarQuery.graphql';
import type { RouteResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

const RESOURCE_TYPE_GLOBAL_PRODUCTS_SETTINGS_SIDEBAR = 'GLOBAL_PRODUCTS_SETTINGS_SIDEBAR';
const SERVICE_DESK = 'jira-servicedesk';
const FREE_EDITION = 'FREE_EDITION';
const STANDARD_EDITION = 'STANDARD_EDITION';

const isWorkforceAemEnabled = (): boolean =>
	fg('jsm_workforce_use_aem') ? isFeatureEnabled('jira-service-management', 'jsm-workforce') : true;

const hasLicensedServiceDeskAboveStandard = ({
	licensedProducts,
	serviceDeskEdition,
}: {
	licensedProducts?: Record<string, boolean | undefined>;
	serviceDeskEdition?: string;
}) =>
	Boolean(licensedProducts?.[SERVICE_DESK]) &&
	serviceDeskEdition !== undefined &&
	serviceDeskEdition !== FREE_EDITION &&
	serviceDeskEdition !== STANDARD_EDITION;

export const nav4SidebarGlobalProductsSettingsResource: RouteResource<
	PreloadedQuery<GlobalProductsSettingsSidebarQuery>
> = createRelayResource<GlobalProductsSettingsSidebarQuery>({
	type: RESOURCE_TYPE_GLOBAL_PRODUCTS_SETTINGS_SIDEBAR,
	getQuery: (_, { tenantContext: { cloudId, appEditions, isAdmin, licensedProducts } }) => ({
		parameters: QUERY,
		variables: {
			cloudId,
			// We can skip the permission check when:
			// 1. The user is an admin (because admins can automatically manage custom onboarding configs), or
			// 2. Upsell is enabled (because upsell is only available to admins), or
			// 3. The main custom onboarding hub gate is disabled (because the custom onboarding feature won't be visible to anyone)
			skipDelegatedAdminCheck:
				isAdmin || appEditions?.software !== 'PREMIUM_EDITION' || !fg('jira-onboarding-hub-gate'),
			skipSiteWfoStatus: !(
				hasLicensedServiceDeskAboveStandard({
					licensedProducts,
					serviceDeskEdition: appEditions?.serviceDesk,
				}) &&
				isWorkforceAemEnabled() &&
				fg('jsm_wfo_skills')
			),
		},
		options: { fetchPolicy: 'store-and-network' as const },
	}),
	captureErrors: true,
	isCritical: true,
});
